import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/formatDate';
import {
  RISK_LABEL, DATA_ACCESS_LABEL, NDA_LABEL, CONTRACT_LABEL, PROPOSAL_KIND_LABEL, ISO_MISSING,
} from './vendorLabels';

// The top of the vendor page answers "is this supplier in order?" before
// anything else: what needs doing (attention) and where it stands (ledger).

const HIGH_RISK = new Set(['high', 'critical']);
export const ndaGap = (v) => HIGH_RISK.has(v.risk_level) && v.nda_status !== 'signed';

// Inline icons, one stroke family, sized by the row's font.
const ICON_PATH = {
  bad: 'M12 8v5m0 3h.01M10.3 3.9 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  warn: 'M12 8v5m0 3h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  info: 'M12 16v-4m0-4h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
};
function Icon({ tone }) {
  return (
    <svg className="vd-signal-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICON_PATH[tone]} />
    </svg>
  );
}

/**
 * Each signal: what is wrong, the one fact that matters, and the fix when the
 * caller can make it. A history that failed to load adds no "pending" signal,
 * so a failed read never claims nothing is pending.
 */
export function buildSignals({ vendor, reviewStatus, effectiveNextReview, history, perms }) {
  const signals = [];
  if (vendor.outside_change_since) {
    signals.push({
      key: 'outside',
      tone: 'warn',
      title: `Changed in Odoo on ${formatDate(vendor.outside_change_since)}, outside Nexus.`,
      detail: 'A speciality owner needs to confirm the change.',
      action: perms.canApprove ? 'acknowledge' : null,
    });
  }
  if (reviewStatus === 'overdue' || reviewStatus === 'due_soon') {
    const due = formatDate(effectiveNextReview || vendor.next_review);
    const never = !vendor.last_review;
    signals.push({
      key: 'review',
      tone: reviewStatus === 'overdue' ? 'bad' : 'warn',
      title: reviewStatus === 'overdue' ? `Review overdue — it was due ${due}.` : `Review due ${due}.`,
      detail: never ? 'This vendor has never been reviewed.' : `Last reviewed ${formatDate(vendor.last_review)}.`,
      action: perms.canReview ? 'review' : null,
    });
  }
  if (ndaGap(vendor)) {
    signals.push({
      key: 'nda',
      tone: 'bad',
      title: `${RISK_LABEL[vendor.risk_level]} risk without a signed NDA.`,
      detail: 'Upload the signed NDA and propose NDA status Signed.',
      action: perms.canPropose ? 'edit' : null,
    });
  }
  if (history.status === 'ready') {
    history.items.filter((p) => p.status === 'pending').forEach((p) => {
      const kind = (PROPOSAL_KIND_LABEL[p.kind] || 'change').toLowerCase();
      signals.push({
        key: `pending-${p.id}`,
        tone: 'info',
        title: `A ${kind === 'edit' ? 'change' : kind} proposed by ${p.proposer_name || 'someone'} on ${formatDate(p.created_at)} is waiting for approval.`,
        detail: 'New edits to this vendor wait until it is decided.',
        action: perms.canApprove ? 'queue' : null,
      });
    });
  }
  return signals;
}

const ACTION_LABEL = {
  acknowledge: 'Acknowledge change',
  review: 'Mark reviewed',
  edit: 'Propose changes',
};

export function VendorAttention({ signals, onAction }) {
  // Nothing outstanding renders nothing: a cleared checklist that keeps talking gets ignored.
  if (!signals.length) return null;
  return (
    <section className="vd-panel vd-attention" aria-labelledby="vd-attention-title">
      <header className="vd-panel-head">
        <h2 id="vd-attention-title" className="vd-panel-title">Needs attention</h2>
        <span className="vd-panel-count">{signals.length}</span>
      </header>
      <ul className="vd-signals">
        {signals.map((s) => (
          <li key={s.key} className={`vd-signal vd-signal--${s.tone}`}>
            <Icon tone={s.tone} />
            <div className="vd-signal-text">
              <p className="vd-signal-title">{s.title}</p>
              <p className="vd-signal-detail">{s.detail}</p>
            </div>
            {s.action === 'queue' && <Link className="vd-text-btn vd-signal-action" to="/vendors#tab=review">Open To review</Link>}
            {s.action && s.action !== 'queue' && (
              <button type="button" className="btn btn-secondary btn-small vd-signal-action" onClick={() => onAction(s.action)}>
                {ACTION_LABEL[s.action]}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Cell({ label, value, sub, tone, empty = false }) {
  return (
    <div className={`vd-ledger-cell ${tone ? `vd-ledger-cell--${tone}` : ''} ${empty ? 'vd-ledger-cell--empty' : ''}`}>
      <dt>{label}</dt>
      <dd>
        <span className="vd-ledger-value">{value}</span>
        {sub && <span className="vd-ledger-sub">{sub}</span>}
      </dd>
    </div>
  );
}

const RISK_TONE = { low: 'ok', medium: 'warn', high: 'bad', critical: 'bad' };
const REVIEW_TONE = { overdue: 'bad', due_soon: 'warn' };

/** Risk, data, NDA, contract and review side by side: the vendor's ISO standing in one line. */
export function VendorCompliance({ vendor, isoFieldsKnown, reviewStatus, effectiveNextReview }) {
  const next = formatDate(effectiveNextReview || vendor.next_review);
  const review = (
    <Cell
      label="Next review"
      value={next || 'Not scheduled'}
      sub={vendor.last_review ? `Last reviewed ${formatDate(vendor.last_review)}` : 'Never reviewed'}
      tone={REVIEW_TONE[reviewStatus]}
    />
  );
  const ndaDate = vendor.nda_date ? `Signed ${formatDate(vendor.nda_date)}` : null;
  const contractEnd = vendor.contract_end ? `Ends ${formatDate(vendor.contract_end)}` : null;
  return (
    <section className="vd-panel vd-ledger" aria-labelledby="vd-ledger-title">
      <header className="vd-panel-head">
        <h2 id="vd-ledger-title" className="vd-panel-title">Compliance</h2>
      </header>
      <dl className="vd-ledger-grid">
        {isoFieldsKnown === false ? (
          <div className="vd-ledger-cell vd-ledger-cell--wide">
            <dt>ISO fields</dt>
            <dd><span className="vd-ledger-sub">{ISO_MISSING}</span></dd>
          </div>
        ) : (
          <>
            <Cell label="Risk" value={RISK_LABEL[vendor.risk_level] || 'Not assessed'} tone={RISK_TONE[vendor.risk_level]} empty={!vendor.risk_level} />
            <Cell label="Data access" value={DATA_ACCESS_LABEL[vendor.data_access_scope] || 'Not set'} empty={!vendor.data_access_scope} />
            <Cell
              label="NDA"
              value={NDA_LABEL[vendor.nda_status] || 'Not set'}
              sub={ndaDate}
              tone={ndaGap(vendor) ? 'bad' : vendor.nda_status === 'signed' ? 'ok' : undefined}
              empty={!vendor.nda_status}
            />
            <Cell
              label="Contract"
              value={CONTRACT_LABEL[vendor.contract_status] || 'Not set'}
              sub={contractEnd}
              tone={vendor.contract_status === 'expired' || vendor.contract_status === 'terminated' ? 'warn' : undefined}
              empty={!vendor.contract_status}
            />
          </>
        )}
        {review}
      </dl>
    </section>
  );
}
